/**
 * Fast boot + streaming hydration helpers.
 * cache → render → hydrate (priority → primary → secondary)
 */
import { loadHomepageCacheSync } from '../api/homepageDataService';
import { mergeUserProfile } from '../api/userProfileService';
import { perfTimeStart, perfTimeEnd } from './homepageHydrationPerf';
import { perfTimeline } from './startupPerf';

export function yieldToMain() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

/**
 * Normalize flat or structured API/cache payload into hydration sections.
 */
export function splitHomepageSections(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      priority: { wallet: null, subscription: null },
      primary: { upcomingGames: [], gamesMeta: null },
      secondary: { profile: null, serverTime: null, cacheHints: null },
    };
  }

  if (raw.priority || raw.primary || raw.secondary) {
    return {
      priority: {
        wallet: raw.priority?.wallet ?? raw.wallet ?? null,
        subscription: raw.priority?.subscription ?? raw.subscription ?? null,
      },
      primary: {
        upcomingGames: raw.primary?.upcomingGames ?? raw.upcomingGames ?? [],
        gamesMeta: raw.primary?.gamesMeta ?? raw.gamesMeta ?? null,
      },
      secondary: {
        profile: raw.secondary?.profile ?? raw.profile ?? null,
        serverTime: raw.secondary?.serverTime ?? raw.serverTime ?? null,
        cacheHints: raw.secondary?.cacheHints ?? raw.cacheHints ?? null,
      },
    };
  }

  return {
    priority: {
      wallet: raw.wallet ?? null,
      subscription: raw.subscription ?? null,
    },
    primary: {
      upcomingGames: raw.upcomingGames ?? [],
      gamesMeta: raw.gamesMeta ?? null,
    },
    secondary: {
      profile: raw.profile ?? null,
      serverTime: raw.serverTime ?? null,
      cacheHints: raw.cacheHints ?? null,
    },
  };
}

/** Merge realtime listener slices without overwriting unrelated fields. */
export function mergePrioritySection(prev, next) {
  if (!next) return prev ?? { wallet: null, subscription: null };
  return {
    wallet: next.wallet
      ? { ...(prev?.wallet ?? {}), ...next.wallet }
      : prev?.wallet ?? null,
    subscription: next.subscription
      ? { ...(prev?.subscription ?? {}), ...next.subscription }
      : prev?.subscription ?? null,
  };
}

export function mergeSecondarySection(prev, next) {
  if (!next) return prev ?? { profile: null, serverTime: null, cacheHints: null };
  return {
    profile: next.profile
      ? mergeUserProfile(prev?.profile ?? null, next.profile)
      : prev?.profile ?? null,
    serverTime: next.serverTime ?? prev?.serverTime ?? null,
    cacheHints: next.cacheHints ?? prev?.cacheHints ?? null,
  };
}

/** Merge sections back to flat payload for persistence. */
export function mergeHomepageSections(sections) {
  const s = sections || splitHomepageSections(null);
  return {
    wallet: s.priority.wallet,
    subscription: s.priority.subscription,
    upcomingGames: s.primary.upcomingGames,
    gamesMeta: s.primary.gamesMeta,
    profile: s.secondary.profile,
    serverTime: s.secondary.serverTime,
    cacheHints: s.secondary.cacheHints,
    priority: s.priority,
    primary: s.primary,
    secondary: s.secondary,
  };
}

/**
 * Step 1 — synchronous cached render (never awaits network).
 * @returns {{ sections: object, fromCache: boolean }}
 */
export function renderCachedState() {
  perfTimeStart('cachedRender');
  const cached = loadHomepageCacheSync();
  if (!cached) {
    perfTimeEnd('cachedRender');
    return { sections: null, fromCache: false };
  }
  const sections = splitHomepageSections(cached);
  perfTimeEnd('cachedRender');
  perfTimeline('hydration.cachedRender', 'render', {
    games: sections.primary.upcomingGames?.length ?? 0,
    hasWallet: Boolean(sections.priority.wallet),
  });
  return { sections, fromCache: true };
}

/**
 * Steps 4–5 — progressive UI hydration after single API response.
 * @param {object} raw
 * @param {{ onPriority?: Function, onPrimary?: Function, onSecondary?: Function }} handlers
 */
export async function hydrateHomepageProgressively(raw, handlers = {}) {
  const sections = splitHomepageSections(raw);

  perfTimeStart('hydration');
  perfTimeStart('hydration.priority');
  await handlers.onPriority?.(sections.priority);
  perfTimeEnd('hydration.priority');
  perfTimeline('hydration.priority', 'api', {
    hasWallet: Boolean(sections.priority.wallet),
    hasSubscription: Boolean(sections.priority.subscription),
  });

  await yieldToMain();

  perfTimeStart('hydration.primary');
  await handlers.onPrimary?.(sections.primary);
  perfTimeEnd('hydration.primary');
  perfTimeline('hydration.primary', 'api', {
    games: sections.primary.upcomingGames?.length ?? 0,
  });

  await yieldToMain();

  perfTimeStart('hydration.secondary');
  await handlers.onSecondary?.(sections.secondary);
  perfTimeEnd('hydration.secondary');
  perfTimeline('hydration.secondary', 'api', {
    hasProfile: Boolean(sections.secondary.profile),
  });

  perfTimeEnd('hydration');
  return mergeHomepageSections(sections);
}
